import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { PrincipalButton } from "../../components/PrincipalButton";
import {
  HiFingerPrint,
  HiLockOpen,
  HiOutlineCheck,
  HiOutlineLockClosed,
  HiOutlineLockOpen,
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineXMark,
  HiPlus,
} from "react-icons/hi2";
import moment from "moment";
import {
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { DataGridBase } from "../../components/DataGrid/DataGridBase";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { SlOptionsVertical } from "react-icons/sl";
import Modal from "../../components/Modal";
import api from "../../api/api";
import { Select } from "../../components/Select";
import { toast } from "react-toastify";
import { MdEdit, MdContentCopy } from "react-icons/md";
import { Input } from "../../components/Input";
import { CustomCheckbox } from "../../components/Checkbox";
import { createPortal } from "react-dom";
import { knowAcces } from "../../utils/utils";

// Formattazione importi in stile italiano (migliaia con ".", decimali con ",").
// Solo display: i valori grezzi restano invariati per confronti/ordinamento.
// I testi non numerici (es. "SOSPESO ADE") vengono restituiti tali e quali.
const formatImporto = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = parseDisplayNumber(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString("it-IT", {
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseDisplayNumber = (value) => {
  if (typeof value === "number") return value;

  const text = String(value).trim();
  if (!text) return NaN;

  if (text.includes(",")) {
    return Number(text.replace(/\s/g, "").replace(/\./g, "").replace(",", "."));
  }

  if (/^-?\d{1,3}(\.\d{3})+$/.test(text)) {
    return Number(text.replace(/\./g, ""));
  }

  return Number(text.replace(/\s/g, ""));
};

// Valore canonico (dot-decimal) -> stringa modificabile in stile IT (virgola
// decimale, senza separatore migliaia), usata mentre il campo e' a fuoco.
const importoToEditable = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value).replace(".", ",");
};

// Stringa digitata in stile IT -> valore canonico dot-decimal da salvare.
const parseImporto = (text) => {
  if (text === null || text === undefined) return "";
  return String(text).replace(/\./g, "").replace(/\s/g, "").replace(",", ".");
};

// Formattazione "live" (mentre si digita): raggruppa le migliaia con "." e
// mantiene la parte decimale digitata (virgola, max 2 cifre) senza forzare ",00".
const formatImportoLive = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const s = String(value);
  const hasComma = s.includes(",");
  let [intRaw, decRaw = ""] = s.split(",");
  intRaw = intRaw.replace(/\D/g, "");
  decRaw = decRaw.replace(/\D/g, "").slice(0, 2);
  const grouped = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return hasComma ? `${grouped},${decRaw}` : grouped;
};

// Campo importo con formattazione migliaia in tempo reale e cursore preservato.
// Mantiene internamente il valore canonico (dot-decimal) per salvataggio/confronti.
const ImportoInput = ({ label, value, onChangeValue, disabled }) => {
  const inputRef = useRef(null);
  const display = formatImportoLive(importoToEditable(value));

  const handleChange = (e) => {
    const el = e.target;
    const before = el.value;
    const caret = el.selectionStart ?? before.length;
    // Caratteri "significativi" = cifre e virgola decimale. I punti delle
    // migliaia (auto-inseriti) NON contano: cosi' aggiungerli/toglierli non
    // sposta il cursore, e la virgola appena digitata lascia il caret dopo di essa.
    const signifBefore = (before.slice(0, caret).match(/[\d,]/g) || []).length;

    // Tiene solo cifre e virgola (scarta lettere come "e", separatori, ecc.)
    const cleaned = before.replace(/[^\d,]/g, "");
    onChangeValue(parseImporto(cleaned));

    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;
      const formatted = node.value;
      let pos = 0;
      let seen = 0;
      while (pos < formatted.length && seen < signifBefore) {
        if (/[\d,]/.test(formatted[pos])) seen++;
        pos++;
      }
      node.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="relative mb-2">
      <label className="text-gray-500 text-xs font-medium uppercase tracking-wider px-1 left-2 -top-2">
        {label} {disabled ? <HiOutlineLockClosed className="inline" /> : ""}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          autoComplete="off"
          inputMode="decimal"
          type="text"
          value={display}
          onChange={handleChange}
          disabled={disabled}
          className={`rounded-md h-10 p-2 pl-2 w-full focus:outline-success-200 ${
            disabled ? "border-2 " : "border border-gray-300"
          }`}
        />
      </div>
    </div>
  );
};

export const ConIncarico = ({
  screenSize,
  f24,
  dataF24,
  giornof24,
  loadGiornoF24,
}) => {
  const [self24, setSelF24] = useState(null);
  const [referenti_esterni, setReferentiEsterni] = useState([]);
  const [elenco_iban, setElencoIban] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef();
  const actionsButton = useRef();
  const [buttonVisible, setButtonVisible] = useState(false);
  const [tipologia_f24, setTipologiaF24] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openBlockModal, setOpenBlockModal] = useState(false);
  const [elencoF24, setElencoF24] = useState([]);
  const [elencoF24Check, setElencoF24Check] = useState([]);
  const [ibanDiego, setIbanDiego] = useState(false);
  const [openBulkModal, setOpenBulkModal] = useState(false);
  const [bulkConfig, setBulkConfig] = useState(null);
  const [openBlockImportModal, setOpenBlockImportModal] = useState(false);

  const isAdmin = knowAcces(['admin']);
  const isTL    = knowAcces(['team_leader']);
  const isPaghe = knowAcces(['paghe']);
  const soloPaghe = isPaghe && !isAdmin && !isTL && !knowAcces(["f24"]);
  const canChangeScope = isTL || (isPaghe && !isAdmin);
  const defaultScope = isTL
    ? 'mine'
    : isAdmin
    ? 'all'
    : isPaghe
    ? 'mine'
    : 'all';
  const [scope, setScope] = useState(defaultScope);
  const scopeButtons = isTL
    ? [['mine', 'Solo miei'], ['team', 'Mio team'], ['all', 'Tutti']]
    : [['mine', 'Solo miei'], ['all', 'Tutti']];
  useEffect(() => { sessionStorage.removeItem('f24_scope'); }, []);

  useEffect(() => {}, [screenSize]);

  const handleToggleButton = () => {
    setButtonVisible(!buttonVisible);
  };

  useEffect(() => {
    if (!f24) return;
    let active = true;
    api.get(`/f24/con_incarico/${f24}`, {
      params: { dataF24, scope },
    }).then((response) => {
      if (active) setElencoF24(response.data);
    }).catch(() => {
      if (active) toast.error("Errore durante il caricamento F24");
    });
    return () => { active = false; };
  }, [f24, dataF24, scope]);

  useEffect(() => {
    loadIbanDiego();
  }, []);

  const loadIbanDiego = async () => {
    const responseiban = await api.get(`/settings/iban_diego`);
    setIbanDiego(responseiban.data[0].iban_diego);
  };
  const loadModal = async (row) => {
    setSelF24(row);
    await loadReferenti(row);
    await loadIban(row);
    await loadAltriF24();
    document.getElementById("dettaglioF24").click();
  };
  const loadReferenti = async (row) => {
    const refEst = await api.get(
      `/referenti_aziende_esterno/${row.codice_univoco}`
    );
    setReferentiEsterni(refEst.data.filter((el) => el.tipologia === 3));
  };
  const loadIban = async (row) => {
    // Converte dataF24 (formato DD/MM/YYYY) in formato YYYY-MM-DD per il backend
    const dataRiferimento = dataF24 ? moment(dataF24, 'DD/MM/YYYY').format('YYYY-MM-DD') : null;
    const elIb = await api.get(`/iban/${row.codice_univoco}`, {
      params: dataRiferimento ? { dataRiferimento } : {}
    });
    setElencoIban(elIb.data);
  };
  const loadAltriF24 = async (row) => {
    const tip_f24 = await api.get(`/tipologia_f24`);

    let data = tip_f24.data;

    // Nasconde "Rifiutato da banca" (id=4) e "Sospeso ADE" (id=6) per utenti PAGHE/SEGRETERIA/ASSUNZIONI
    if (!knowAcces(["f24", "team_leader", "admin"])) {
      data = data.filter((el) => el.id != 4 && el.id != 6);
    }

    setTipologiaF24(data);
  };

  const columns = [
    {
      field: "inizio_incarico",
      headerName: "Data inizio incarico",
      type: "date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) =>
        params.row.inizio_incarico
          ? new Date(params.row.inizio_incarico)
          : null,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY") : "",
    },
    {
      field: "fine_incarico",
      headerName: "Data fine incarico",
      type: "date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) =>
        params.row.fine_incarico
          ? new Date(params.row.fine_incarico)
          : null,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY") : "",
    },
    {
      field: "codice_univoco",
      headerName: "Codice Univoco",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "ragione_sociale",
      headerName: "Ragione Sociale",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "descrizione_payroll",
      headerName: "Referente",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "tipologia_serv",
      headerName: "Tipologia",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "Email",
      headerName: "Email",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.value) return "";
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              width: "100%",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.value}
            </span>
            <button
              type="button"
              title="Copia email"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard
                  .writeText(params.value)
                  .then(() => toast.success("Email copiata"))
                  .catch(() => toast.error("Impossibile copiare l'email"));
              }}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdContentCopy size={16} />
            </button>
          </div>
        );
      },
    },
    {
      field: "iban",
      headerName: "IBAN",
      width: 255,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "importo",
      headerName: "Importo",
      flex: 1,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        params.row.id_stato_extra > 0
          ? params.row.desc_stato_f24
          : params.row.importo,
      valueFormatter: (params) => formatImporto(params.value),
    },
    {
      field: "pagamento_parziale",
      headerName: "Pagamento Parziale",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) =>
        params.row.giorno_f24 && params.row.pagamento_parziale === 1 ? 1 : "",
      renderCell: (params) => {
        if (params.value === 1) {
          return <CustomCheckbox value={true} />;
        } else {
          return "";
        }
      },
    },
    {
      field: "pagato",
      headerName: "Versato",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) =>
        params.row.giorno_f24 && params.row.pagato === 1 ? 1 : "",
      renderCell: (params) => {
        if (params.value === 1) {
          return <CustomCheckbox value={true} />;
        } else {
          return "";
        }
      },
    },
    {
      field: "quietanza_disponibile",
      headerName: "Quietanza disponibile",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) =>
        params.row.giorno_f24 && params.row.quietanza_disponibile === 1 ? 1 : "",
      renderCell: (params) => {
        if (params.value === 1) {
          return <CustomCheckbox value={true} />;
        } else {
          return "";
        }
      },
    },
    {
      field: "nota",
      headerName: "Nota",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        params.value ? (
          <span
            title={params.value}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {params.value}
          </span>
        ) : (
          ""
        ),
    },
  ];

  // La verifica usa lo stesso perimetro della tabella principale, già filtrata dal backend.
  const codiciF24Visibili = new Set(elencoF24.map((row) => row.codice_univoco));
  const righeCheckVisibili = elencoF24Check.filter((row) =>
    codiciF24Visibili.has(row.codice_univoco)
  );

  const columnsCheckF24 = [
    {
      field: "codice_univoco",
      headerName: "Codice Univoco",
      width: 145,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "ragione_sociale",
      headerName: "Ragione Sociale",
      minWidth: 210,
      flex: 1.4,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "referente_payroll1",
      headerName: "Referente paghe",
      minWidth: 190,
      flex: 1.15,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "importo_inserito",
      headerName: "Importo inserito",
      minWidth: 145,
      flex: 0.85,
      headerAlign: "right",
      align: "right",
      cellClassName: (params) => {
        if (params.value) {
          return "goodCell";
        } else {
          return "badCell";
        }
      },
      valueFormatter: (params) => formatImporto(params.value),
    },
    {
      field: "importo_da_zucchetti",
      headerName: "Importo ricevuto",
      minWidth: 145,
      flex: 0.85,
      headerAlign: "right",
      align: "right",
      cellClassName: (params) => {
        if (params.value) {
          return "goodCell";
        } else {
          return "badCell";
        }
      },
      valueFormatter: (params) => formatImporto(params.value),
    },
    {
      field: "iban_inserito",
      headerName: "IBAN inserito",
      minWidth: 245,
      flex: 1.3,
      headerAlign: "left",
      align: "left",
      cellClassName: (params) => {
        if (params.value) {
          return "goodCell";
        } else {
          return "badCell";
        }
      },
    },
    {
      field: "iban_da_zucchetti",
      headerName: "IBAN ricevuto",
      minWidth: 245,
      flex: 1.3,
      headerAlign: "left",
      align: "left",
      cellClassName: (params) => {
        if (params.value) {
          return "goodCell";
        } else {
          return "badCell";
        }
      },
    },
    {
      field: "stato",
      headerName: "Stato",
      minWidth: 150,
      flex: 0.85,
      headerAlign: "center",
      align: "center",
      cellClassName: (params) => {
        if (params.value == "CORRETTO") {
          return "goodCellStato";
        } else {
          return "badCell";
        }
      },
    },
    {
      field: "nota",
      headerName: "Nota",
      minWidth: 180,
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        params.value ? (
          <span
            title={params.value}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {params.value}
          </span>
        ) : (
          ""
        ),
    },
  ];

  const checkf24 = async () => {
    try {
      setLoading(true);
      setOpenModal(true);
      const f24 = await api.get(`/f24/checkF24`, {
        params: { giornof24: giornof24 },
      });

      setElencoF24Check(f24.data);

      setLoading(false);
    } catch {
      toast.error("Errore durante il caricamento");
    }
  };

  const blockEdit = () => {
    setOpenBlockModal(true);
  };

  const handleBulkFlag = (flag, value) => {
    setBulkConfig({ flag, value });
    setOpenBulkModal(true);
  };

  const confirmBulkFlag = async () => {
    try {
      await api.put("/f24/bulk_flag", {
        giornof24Id: giornof24?.ID,
        flag: bulkConfig.flag,
        value: bulkConfig.value,
      });
      const res = await api.get(`/f24/con_incarico/${f24}`, {
        params: { dataF24, scope },
      });
      setElencoF24(res.data);
      toast.success("Aggiornamento massivo completato");
    } catch {
      toast.error("Errore durante l'aggiornamento massivo");
    } finally {
      setOpenBulkModal(false);
      setBulkConfig(null);
    }
  };

  const handleDayBlock = async () => {
    try {
      let update = await api.put(`/f24/blocco/${giornof24?.ID}`, {
        stato: giornof24?.inibito == 1 ? 0 : 1,
      });
      loadGiornoF24();
      toast.success(
        giornof24?.inibito == 1
          ? "sbloccato con successo"
          : "bloccato con successo"
      );
      setOpenBlockModal(false);
    } catch {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleBlockImport = async () => {
    try {
      await api.put(`/f24/blocco_import/${giornof24?.ID}`, {
        stato: giornof24?.blocco_import == 1 ? 0 : 1,
      });
      loadGiornoF24();
      toast.success(
        giornof24?.blocco_import == 1
          ? "Import sbloccato con successo"
          : "Import bloccato con successo"
      );
      setOpenBlockImportModal(false);
    } catch {
      toast.error("Errore durante il salvataggio");
    }
  };

  function CustomToolbar(btn) {
    return (
      <GridToolbarContainer>
        {canChangeScope && (btn || !soloPaghe) && (
          <div className="w-full flex flex-wrap gap-x-1 gap-y-1 mb-1">
            {scopeButtons.map(([s, label]) => (
              <button key={s} onClick={() => setScope(s)}
                className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${scope === s ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:border-blue-200'}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="w-full flex flex-wrap-reverse md:flex-nowrap justify-between md:space-x-5 mb-2 gap-y-1">
          <div className="flex md:flex-nowrap md:w-fit w-full flex-wrap justify-center md:justify-start gap-x-2">
            <GridToolbarQuickFilter placeholder="Cerca..." />
          </div>
          {btn && knowAcces(["admin", "f24", "team_leader"]) && (
            <div className="flex flex-wrap justify-center md:justify-end md:mx-0 mx-auto gap-2 items-center">
              {giornof24?.inibito != 1 && (
                <>
                  {/* Gruppo Versato */}
                  <div className="flex gap-1 border border-miller-300 rounded-md p-0.5">
                    <Button
                      className="success-button border-0"
                      size="md"
                      text={"Versato"}
                      icon={<HiOutlineCheck className="button-icon mr-1" />}
                      onClick={() => handleBulkFlag("pagato", 1)}
                    />
                    <div className="w-px bg-miller-300 my-1" />
                    <Button
                      className="error-button border-0"
                      size="md"
                      text={"Versato"}
                      icon={<HiOutlineXMark className="button-icon mr-1" />}
                      onClick={() => handleBulkFlag("pagato", 0)}
                    />
                  </div>
                  {/* Gruppo Quietanza */}
                  <div className="flex gap-1 border border-miller-300 rounded-md p-0.5">
                    <Button
                      className="success-button border-0"
                      size="md"
                      text={"Quietanza"}
                      icon={<HiOutlineCheck className="button-icon mr-1" />}
                      onClick={() => handleBulkFlag("quietanza_disponibile", 1)}
                    />
                    <div className="w-px bg-miller-300 my-1" />
                    <Button
                      className="error-button border-0"
                      size="md"
                      text={"Quietanza"}
                      icon={<HiOutlineXMark className="button-icon mr-1" />}
                      onClick={() => handleBulkFlag("quietanza_disponibile", 0)}
                    />
                  </div>
                  <div className="w-px h-6 bg-gray-300" />
                </>
              )}
              <Button
                className={giornof24?.inibito == 1 ? "warning-button" : "success-button"}
                size="md"
                text={giornof24?.inibito == 1 ? "Modifiche bloccate" : "Modifiche sbloccate"}
                icon={
                  giornof24?.inibito == 1 ? (
                    <HiOutlineLockClosed className="button-icon mr-2" />
                  ) : (
                    <HiOutlineLockOpen className="button-icon mr-2" />
                  )
                }
                onClick={blockEdit}
              />
              <Button
                className={giornof24?.blocco_import == 1 ? "warning-button" : "success-button"}
                size="md"
                text={giornof24?.blocco_import == 1 ? "Import bloccato" : "Import sbloccato"}
                icon={
                  giornof24?.blocco_import == 1 ? (
                    <HiOutlineLockClosed className="button-icon mr-2" />
                  ) : (
                    <HiOutlineLockOpen className="button-icon mr-2" />
                  )
                }
                onClick={() => setOpenBlockImportModal(true)}
              />
            </div>
          )}
          {btn && knowAcces(["admin", "f24", "team_leader", "paghe"]) && (
            <Button
              className="success-button"
              size="md"
              text={"Check F24"}
              icon={<HiFingerPrint className="button-icon " />}
              onClick={checkf24}
            />
          )}
        </div>
      </GridToolbarContainer>
    );
  }

  const bulkFlagLabel =
    bulkConfig?.flag === "pagato" ? "Versato" : "Quietanza disponibile";
  const bulkActionLabel = bulkConfig?.value === 1 ? "selezionare" : "deselezionare";

  return (
    <>
      {openBulkModal &&
        createPortal(
          <Modal
            isOpen={openBulkModal}
            closeButton={() => {
              setOpenBulkModal(false);
              setBulkConfig(null);
            }}
            leftButtons={
              <Button
                className="success-button"
                size="md"
                text={"Conferma"}
                icon={<HiOutlineCheck className="button-icon" />}
                onClick={confirmBulkFlag}
              />
            }
          >
            <div className="el-container">
              Attenzione, sei sicuro di voler <strong>{bulkActionLabel}</strong> massivamente il flag{" "}
              <strong>"{bulkFlagLabel}"</strong> per tutte le aziende con importo numerico del mese{" "}
              <strong>{dataF24}</strong>?
            </div>
          </Modal>,
          document.getElementById("portalModal")
        )}

      {openBlockImportModal &&
        createPortal(
          <Modal
            isOpen={openBlockImportModal}
            closeButton={() => setOpenBlockImportModal(false)}
            leftButtons={
              <Button
                className="success-button"
                size="md"
                text={"Conferma"}
                icon={<HiOutlineCheck className="button-icon" />}
                onClick={handleBlockImport}
              />
            }
          >
            <div className="el-container">
              {giornof24?.blocco_import == 1
                ? "Attenzione, sei sicuro di voler sbloccare l'import di Importo e IBAN da Zucchetti per il mese selezionato?"
                : "Attenzione, sei sicuro di voler bloccare l'import di Importo e IBAN da Zucchetti? I dati verranno cristallizzati alla situazione attuale."}
            </div>
          </Modal>,
          document.getElementById("portalModal")
        )}

      {openBlockModal &&
        createPortal(
          <Modal
            isOpen={openBlockModal}
            closeButton={() => setOpenBlockModal(false)}
            leftButtons={
              <Button
                className="success-button"
                size="md"
                text={"Conferma"}
                icon={<HiOutlineCheck className="button-icon" />}
                onClick={handleDayBlock}
              />
            }
          >
            <div className="el-container ">
              {giornof24?.inibito == 1
                ? "Attenzione, sei sicuro di voler sbloccare le modifiche per il mese selezionato?"
                : "Attenzione, sei sicuro di voler inibire le modifiche per il mese selezionato?"}
            </div>
          </Modal>,
          document.getElementById("portalModal")
        )}

      {openModal &&
        createPortal(
          <Modal
            isOpen={openModal}
            closeButton={() => setOpenModal(false)}
            headerText="Verifica importi e IBAN F24"
            fullHeight
          >
            {loading ? (
              <center className="animate-pulse">Caricamento</center>
            ) : (
              <div className="el-container h-full min-h-[520px]">
                <DataGridBase
                  slots={{ toolbar: () => CustomToolbar(false) }}
                  rowHeight={42}
                  columnHeaderHeight={48}
                  sx={{
                    border: 0,
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #cbd5e1",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 700,
                      color: "#334155",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#f8fafc80",
                    },
                    "& .MuiDataGrid-cell": {
                      borderColor: "#e2e8f0",
                      px: 1.5,
                    },
                  }}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'ragione_sociale', sort: 'asc' }],
                    },
                  }}
                  columns={columnsCheckF24}
                  rows={righeCheckVisibili}
                  getRowId={(r) => r.codice_univoco}
                />
              </div>
            )}
          </Modal>,
          document.getElementById("portalModal")
        )}
      <ModalF24
        tipologia={"inserimento"}
        ibanDiego={ibanDiego}
        giornof24={giornof24}
        tipologia_f24={tipologia_f24}
        idF24={f24}
        self24={self24}
        dataF24={dataF24}
        elenco_iban={elenco_iban}
        referenti_esterni={referenti_esterni}
        setElencoF24={setElencoF24}
        scope={scope}
      />
      <div className="el-container h-[300px] md:h-[80vh]">
        <DataGridBase
          storageKey="f24_con_incarico"
          defaultHiddenColumns={{ inizio_incarico: false, fine_incarico: false }}
          slots={{ toolbar: () => CustomToolbar(true) }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'ragione_sociale', sort: 'asc' }],
            },
          }}
          columns={columns}
          rows={elencoF24}
          getRowId={(r) => r.codice_univoco}
          onRowClick={async (params) => {
            await loadModal(params.row);
          }}
        />
      </div>
    </>
  );
};

const ModalF24 = ({
  tipologia_f24,
  idF24,
  tipologia,
  self24,
  dataF24,
  elenco_iban,
  referenti_esterni,
  setElencoF24,
  giornof24,
  ibanDiego,
  scope,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedF24, setSelectedF24] = useState(null);

  const handleClickOpen = async () => {
    let s = { ...self24 };

    // commento perché sembra sbagliata come logica
    // if (!s.giorno_f24 && elenco_iban.length > 0) {
    //   s.id_iban = elenco_iban[0].ID;
    // }

    if (self24.ibanDiego == 1) {
      // se l'iban è quello di diego
    }

    setSelectedF24(s);
    setOpen(true);
  };

  const handleChange = (e) => {
    let sf24 = { ...selectedF24 };
    sf24[e.target.name] = e.target.value;
    setSelectedF24(sf24);
  };

  const handleChangeSelect = (e, name, updateimp) => {
    let sf24 = { ...selectedF24 };
    const SOSPESO_ADE_ID = 6;
    const oldStatus = parseInt(sf24[name]);
    sf24[name] = e.target.value;
    if (updateimp) {
      const newStatus = parseInt(e.target.value);
      // Preserva importo quando si seleziona "Sospeso ADE" o si ritorna da esso
      if (newStatus != SOSPESO_ADE_ID && oldStatus != SOSPESO_ADE_ID) {
        sf24.importo = 0;
      }
    }
    setSelectedF24(sf24);
  };

  const handleChangeCheckbox = (e, name) => {
    let sf24 = { ...selectedF24 };
    sf24[name] = e.target.checked ? 1 : 0;
    setSelectedF24(sf24);
  };

  const handleSave = async () => {
    // Importo e IBAN sono vincolati: se c'e' uno deve esserci anche l'altro.
    // Non si applica agli stati speciali (id_stato_extra), dove l'importo e'
    // gestito/disabilitato dallo stato.
    if (!(selectedF24?.id_stato_extra > 0)) {
      const hasImporto = Number(selectedF24?.importo) > 0;
      const hasIban = Number(selectedF24?.id_iban) > 0;
      if (hasImporto && !hasIban) {
        toast.error("Hai inserito un importo: seleziona anche l'IBAN.");
        return;
      }
    }
    try {
      if (selectedF24.giorno_f24) {
        await api.put("/f24", { f24: selectedF24 });
        toast.success("f24 modificato con successo");
      } else {
        await api.post("/f24", { f24: selectedF24, idF24: idF24 });
        toast.success("f24 creato con successo");
      }

      // Ricarica i dati dal backend per avere tutti i campi aggiornati (inclusi JOIN)
      const responsef24 = await api.get(`/f24/con_incarico/${idF24}`, {
        params: { dataF24: dataF24, scope: scope || 'all' },
      });
      setElencoF24(responsef24.data);

      // Chiudi la modale dopo il salvataggio riuscito
      setOpen(false);
    } catch {
      toast.error("errore durante il salvataggio");
    }
  };

  // La nota e' inseribile/modificabile solo da F24, team leader e ADMIN (per ruolo);
  // mostrata in sola lettura a tutti.
  const canEditNota = knowAcces(["f24", "admin", "team_leader"]);

  const handleSaveNota = async () => {
    try {
      await api.put("/f24/nota", {
        giornof24Id: giornof24?.ID,
        codice_univoco: selectedF24?.codice_univoco,
        nota: selectedF24?.nota ?? "",
        utente: localStorage.getItem("username"),
      });
      toast.success("Nota salvata con successo");
      const responsef24 = await api.get(`/f24/con_incarico/${idF24}`, {
        params: { dataF24: dataF24 },
      });
      setElencoF24(responsef24.data);
    } catch {
      toast.error("Errore durante il salvataggio della nota");
    }
  };

  return (
    <>
      <Button
        id="dettaglioF24"
        className="hidden"
        text="Aggiungi"
        type="button"
        icon={<HiPlus className="button-icon" />}
        onClick={async () => {
          await handleClickOpen();
        }}
      />

      <Modal
        isOpen={open}
        closeButton={() => setOpen(false)}
        disableOverlayClose
        headerText={self24?.ragione_sociale + " - " + dataF24}
        leftButtons={
          <Button
            className="principal-button z-[110]"
            text={tipologia === "elimina" ? "Elimina" : "Salva"}
            type="button"
            icon={<HiOutlineCheck className="button-icon" />}
            onClick={() => handleSave()}
          />
        }
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 md:col-span-1">
            <ImportoInput
              label={"importo (F24)"}
              value={selectedF24?.importo}
              onChangeValue={(v) =>
                setSelectedF24({ ...selectedF24, importo: v })
              }
              disabled={
                giornof24?.inibito == 1 ||
                selectedF24?.id_stato_extra > 0 ||
                selectedF24?.pagato == 1 ||
                !knowAcces(["admin", "f24", "team_leader", "paghe"])
              }
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Select
              type="text"
              label={"Iban"}
              name="id_iban"
              //onChange={(e) => handleChange(e)}
              value={selectedF24?.id_iban}
              onChange={(e) => handleChangeSelect(e, "id_iban", false)}
              placeholder="SELEZIONA..."
              options={elenco_iban.map((el) => {
                const nameIban = el.note ? el.iban + " - " + el.note : el.iban;
                return { id: el.ID, name: nameIban };
              })}
              disabled={
                giornof24?.inibito == 1 ||
                !knowAcces(["admin", "f24", "team_leader", "paghe"])
              }
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <Select
              type="text"
              label={"Altri Stati F24"}
              name="id_stato_extra"
              value={selectedF24?.id_stato_extra}
              //onChange={(e) => handleChange(e)}
              //value={cliente.tipologia_servizio_assunzioni}
              onChange={(e) => handleChangeSelect(e, "id_stato_extra", true)}
              placeholder="SELEZIONA..."
              options={tipologia_f24}
              disabled={
                giornof24?.inibito == 1 ||
                !knowAcces(["admin", "f24", "team_leader", "paghe"])
              }
            />
          </div>

          <div className="col-span-2 md:col-span-1 pt-5">
            <CustomCheckbox
              label="pagamento parziale"
              name="pagamento_parziale"
              onClick={(e) => handleChangeCheckbox(e, "pagamento_parziale")}
              value={selectedF24?.pagamento_parziale === 1}
              disabled={
                giornof24?.inibito == 1 ||
                !knowAcces(["admin", "f24", "team_leader", "paghe"])
              }
            />
            <CustomCheckbox
              label="Versato"
              name="pagato"
              onClick={(e) => handleChangeCheckbox(e, "pagato")}
              value={selectedF24?.pagato === 1}
              disabled={
                giornof24?.inibito == 1 ||
                !knowAcces(["admin", "f24", "team_leader"])
              }
            />
            <CustomCheckbox
              label="quietanza Disponibile"
              name="quietanza_disponibile"
              onClick={(e) => handleChangeCheckbox(e, "quietanza_disponibile")}
              value={selectedF24?.quietanza_disponibile === 1}
              disabled={
                giornof24?.inibito == 1 ||
                !knowAcces(["admin", "f24", "team_leader", "segreteria"])
              }
            />
          </div>

          <div className="col-span-2 mt-2">
            <label className="text-sm font-semibold dark:text-slate-200">
              Nota mese
            </label>
            <textarea
              name="nota"
              rows={3}
              className="w-full rounded-md ring-1 dark:ring-miller-300 bg-white px-3 py-2 mt-1 disabled:opacity-70"
              placeholder={
                canEditNota
                  ? "Inserisci una nota per questo cliente/mese..."
                  : "Nessuna nota"
              }
              value={selectedF24?.nota || ""}
              onChange={(e) => handleChange(e)}
              disabled={!canEditNota}
            />
            {selectedF24?.nota_utente && (
              <p className="text-xs text-gray-500 mt-1">
                Ultima modifica: {selectedF24.nota_utente}
                {selectedF24.nota_data
                  ? " - " +
                    moment(selectedF24.nota_data).format("DD/MM/YYYY HH:mm")
                  : ""}
              </p>
            )}
            {canEditNota && (
              <div className="flex justify-end mt-2">
                <Button
                  className="success-button"
                  text="Salva nota"
                  type="button"
                  icon={<HiOutlineCheck className="button-icon" />}
                  onClick={handleSaveNota}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
